import { Injectable } from '@angular/core';
import { ConfigService } from '../config/config.service';

@Injectable({
  providedIn: 'root',
})
export class CryptoService {
  public pubKey: string = '';
  public saltKey: string = '';

  constructor(private config: ConfigService) {
  }

  // =====================================================================
  // 🔵 URL PARAMETER ENCRYPT / DECRYPT
  // =====================================================================

  async encryptId(value: string) {
    let encrypted = await this.encryptPayload(value);
    return encrypted.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, 'e4Q3u2A1l');
  }

  async decryptId(value: string) {
    let str = value
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .replace(/e4Q3u2A1l/g, '=');
    return await this.decryptPayload(str) as string;
  }

  // =====================================================================
  // 🔵 AES-GCM PAYLOAD ENCRYPT / DECRYPT (Web Crypto API)
  // =====================================================================
  // Uses Web Crypto API for browser-native security.
  // For production, replace the key derivation with a proper KMS-managed key.

  // NOTE: `crypto` in this file is the crypto-js import; use the Web Crypto
  // global (`webCrypto`) for the AES-GCM primitives below.
  private readonly webCrypto = globalThis.crypto;

  private async getGcmKey(): Promise<CryptoKey> {
    // The key is (URL-safe) base64 encoding 32 raw bytes — decode it to bytes,
    // do NOT TextEncode the string. Must match the backend's key handling.
    const b64 = this.config.encryptionKey.replace(/-/g, '+').replace(/_/g, '/');
    const keyBytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    return this.webCrypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['encrypt', 'decrypt']);
  }

  async encryptPayload(data: unknown): Promise<string> {
    const key = await this.getGcmKey();
    const iv = this.webCrypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(JSON.stringify(data));
    const encrypted = await this.webCrypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    // URL-safe base64, no padding — matches the backend token format.
    return btoa(String.fromCharCode(...combined))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  async decryptPayload<T>(ciphertext: string): Promise<T> {
    const key = await this.getGcmKey();
    // Accept URL-safe base64 and restore padding before decoding.
    let b64 = ciphertext.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const combined = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const data = combined.slice(12);
    const decrypted = await this.webCrypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
    return JSON.parse(new TextDecoder().decode(decrypted)) as T;
  }

  /**
   * Decrypt without throwing. Returns `null` when the input is empty, not valid
   * base64, or wasn't encrypted with this key (e.g. stale data from a previous
   * scheme, or a plaintext response). Mirrors the old doubleDecrypt's tolerance.
   */
  async tryDecryptPayload<T>(ciphertext: string | null | undefined): Promise<T | null> {
    if (!ciphertext) return null;
    try {
      return await this.decryptPayload<T>(ciphertext);
    } catch {
      return null;
    }
  }
}
